import { Alert, AlertTitle, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import moment from 'moment';
import { useState } from 'react';
import {
  AutocompleteInput,
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectInput,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
} from 'react-admin';
import { Permission, ReportList } from '../../components';
import { MonthYearInput } from '../../components/Inputs';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from '../../constants';
import { ClientBillType } from '../../models/clientBillType';
import { GestaoClickStore } from '../../models/gestaoClickStore';
import { filterToQuery } from '../../utils';

/**
 * Meses futuros liberados no seletor de competência.
 *
 * A competência é o mês do vencimento, e o boleto é emitido com antecedência — em agosto já existem
 * cobranças com competência em setembro e outubro. Travar o seletor no mês atual esconde essas
 * cobranças do relatório.
 */
const MESES_DE_ANTECEDENCIA = 2;

const titleSx = {
  margin: '10px 0 10px 0',
  textAlign: 'center',
  fontWeight: 'bold',
};

/**
 * Tipos de inconsistência devolvidos pelo endpoint /reports/billing-issues.
 * A competência é o mês do vencimento, então `duplicate` e `missing_month` apontam problema real
 * no ERP (boleto duplicado, reemitido sem cancelar o antigo). `competence_mismatch` é cobrança cujo
 * mês gravado aqui não bate com o vencimento — o sync ainda não passou nela.
 */
const ISSUE_TYPES = {
  duplicate: { label: 'Cobrança duplicada', color: 'error' },
  missing_month: { label: 'Mês sem cobrança', color: 'warning' },
  competence_mismatch: { label: 'Mês divergente do vencimento', color: 'info' },
  orphan: { label: 'Boleto sumiu do ERP', color: 'default' },
};

const ISSUE_TYPE_CHOICES = Object.entries(ISSUE_TYPES).map(([id, { label }]) => ({ id, name: label }));

const openInGestaoClick = (gestaoClickId) => {
  window.open(
    `https://gestaoclick.com/financeiro/movimentacoes_financeiras/visualizar_recebimento/${gestaoClickId}?retorno=%2Fmovimentacoes_financeiras%2Findex_recebimento`,
    '_blank'
  );
};

const BillingIssuesActions = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const handleInvokeBilling = async () => {
    setLoading(true);
    try {
      await dataProvider.create('partner-payments/invoke-billing', { data: {} });
      notify('Cobranças atualizadas com sucesso!', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Erro ao atualizar cobranças', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TopToolbar sx={{ alignItems: 'center' }}>
      <Button onClick={handleInvokeBilling} disabled={loading} startIcon={<SyncIcon />} size="small">
        {loading ? 'Atualizando...' : 'Atualizar Cobranças'}
      </Button>
    </TopToolbar>
  );
};

const EditClientBillButton = ({ record }) => {
  // `missing_month` não tem cobrança pra editar — o id é sintético.
  if (!record || record.issueType === 'missing_month') return null;
  // Nova aba: a lista de inconsistências (filtros, página) fica aberta enquanto corrige.
  return (
    <Tooltip title="Editar cobrança">
      <IconButton
        size="small"
        component="a"
        href={`#/client-bills/${record.id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const ListBillingIssues = () => {
  const props = useListContext();

  let data = [];
  let summaryData = [];

  if (props.data) {
    const reportInfo = props.data[0];
    if (reportInfo) {
      data = reportInfo.data || [];
      summaryData = reportInfo.summaryData || [];
    }
  }

  return (
    <>
      <Alert severity="info" sx={{ m: 2 }}>
        <AlertTitle>Como corrigir</AlertTitle>
        A competência é o mês do vencimento do boleto no Gestão Click. Para corrigir, abra o boleto no
        ERP pelo botão <strong> Gestão Click</strong>, cancele o boleto duplicado ou ajuste o
        <strong> vencimento</strong> lá e volte aqui para clicar em <strong>Atualizar Cobranças</strong> — o
        mês do nosso lado é reescrito a partir do ERP. Editar a competência só aqui não resolve: a
        próxima sincronização sobrescreve.
      </Alert>

      <Typography sx={titleSx}>Totais por problema</Typography>
      <Datagrid bulkActionButtons={false} style={{ marginBottom: 40 }} data={summaryData} total={1}>
        <FunctionField
          label="Problema"
          sortable={false}
          render={record => ISSUE_TYPES[record?.issueType]?.label || record?.issueType}
        />
        <NumberField source="count" label="Quantidade" textAlign="center" sortable={false} />
        <NumberField
          source="total"
          label="Valor Total"
          textAlign="center"
          emptyText="R$ 0,00"
          sortable={false}
          options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
      </Datagrid>

      <Typography sx={titleSx}>Ocorrências</Typography>
      <Datagrid resource={props.resource} data={data} bulkActionButtons={false}>
        <FunctionField
          label="Problema"
          sortable={false}
          render={record => {
            const issue = ISSUE_TYPES[record?.issueType];
            return <Chip size="small" color={issue?.color || 'default'} label={issue?.label || record?.issueType} />;
          }}
        />
        <ReferenceField label="Rede" source="chainId" reference="chains" link={false} sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Posto" source="placeId" reference="stations" sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <TextField source="monthDate" label="Competência" textAlign="center" sortable={false} />
        <DateField source="competenceDate" label="Emissão no ERP" textAlign="center" emptyText="--" locales="pt-BR" sortable={false} />
        <SelectField
          source="type"
          label="Tipo"
          textAlign="center"
          sortable={false}
          emptyText="—"
          choices={ClientBillType.ClientBillTypeChoices}
        />
        <SelectField
          source="gestaoClickStoreId"
          label="Loja"
          textAlign="center"
          sortable={false}
          emptyText="—"
          choices={GestaoClickStore.GestaoClickStoreChoices}
        />
        <NumberField
          source="value"
          label="Valor"
          textAlign="center"
          emptyText="—"
          sortable={false}
          options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
        />
        <DateField source="dueDate" label="Vencimento" textAlign="center" emptyText="--" locales="pt-BR" sortable={false} />
        <TextField source="detail" label="Detalhe" sortable={false} />
        <FunctionField
          label="Gestão Click"
          textAlign="center"
          sortable={false}
          render={record => record?.gestaoClickId ? (
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={(e) => { e.stopPropagation(); openInGestaoClick(record.gestaoClickId); }}
            >
              Abrir
            </Button>
          ) : null}
        />
        <FunctionField label="" render={record => <EditClientBillButton record={record} />} />
      </Datagrid>
    </>
  );
};

const ListFilters = props => {
  // filterValues vem do contexto da lista, não das props — o Filter é montado pelo ListToolbar e
  // nem sempre recebe os valores por prop.
  const { filterValues } = useListContext();

  return (
  <Filter {...props}>
    <MonthYearInput
      source="startMonth"
      label="Competência Inicial"
      format={date => moment(date).startOf('month').toISOString()}
      maxDate={moment().add(MESES_DE_ANTECEDENCIA, 'month')}
      alwaysOn
    />
    <MonthYearInput
      source="endMonth"
      label="Competência Final"
      format={date => moment(date).startOf('month').toISOString()}
      maxDate={moment().add(MESES_DE_ANTECEDENCIA, 'month')}
      alwaysOn
    />
    <SelectInput
      source="issueType"
      label="Problema"
      choices={ISSUE_TYPE_CHOICES}
      emptyText="Todos"
      alwaysOn
    />
    <ReferenceInput source="chainId" reference="chains" sort={{ field: 'name', order: 'ASC' }} perPage={null} alwaysOn>
      <AutocompleteInput filterToQuery={filterToQuery} label="Rede" optionText="name" />
    </ReferenceInput>
    <ReferenceInput
      source="placeId"
      reference="stations"
      sort={{ field: 'name', order: 'ASC' }}
      perPage={null}
      // Quando há rede selecionada, a lista de postos vem filtrada por ela.
      filter={filterValues?.chainId ? { chainId: filterValues.chainId } : undefined}
      alwaysOn
    >
      <AutocompleteInput filterToQuery={filterToQuery} label="Posto" optionText="name" />
    </ReferenceInput>
    <SelectInput
      source="gestaoClickStoreId"
      label="Loja"
      choices={GestaoClickStore.GestaoClickStoreChoices}
      emptyText="Todas"
      alwaysOn
    />
  </Filter>
  );
};

const ReportBillingIssues = props => {
  const defaultStart = moment().subtract(2, 'months').startOf('month').toISOString();
  const defaultEnd = moment().startOf('month').toISOString();

  return (
    <Permission permission={PERMISSIONS.INVOICES_REPORT}>
      <ReportList
        {...props}
        filterDefaultValues={{ startMonth: defaultStart, endMonth: defaultEnd }}
        basePath="report-billing-issues"
        resource="reports/billing-issues"
        title="Inconsistências de Cobrança"
        icon={<ReceiptLongIcon />}
        filters={<ListFilters />}
        actions={<BillingIssuesActions />}
        perPage={25}
        pagination={<ListPagination />}
        titleOnPrint="Inconsistências de Cobrança"
      >
        <ListBillingIssues />
      </ReportList>
    </Permission>
  );
};

export default ReportBillingIssues;
